import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  getQADashboard, 
  getCallsForReview, 
  getQAMetrics, 
  exportQAReport 
} from '../services/qaService';
import { format, subDays } from 'date-fns';

const SCORE_COLORS = {
  excellent: '#2196F3',  // Blue - 80%+
  good: '#2196F3',       // Blue - 80%+  
  average: '#FFD700',    // Yellow - 60-79%
  poor: '#F44336'        // Red - <60%
};

const CRITERIA_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1'];

const QADashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [calls, setCalls] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState({
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    agent: '',
    campaign: '',
    disposition: '',
    scoreBand: ''
  });
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [dispositions, setDispositions] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard analytics
      const dashboardResponse = await getQADashboard(filters);
      setDashboardData(dashboardResponse.data);

      // Fetch calls for review - show all calls (both reviewed and awaiting review)
      const callsResponse = await getCallsForReview({
        ...filters,
        limit: 50,
        status: 'completed,analyzed,transcribed', // Include transcribed calls
        reviewed: 'all' // Show all calls including those awaiting QA review
      });
      setCalls(callsResponse.data || []);

      // Extract unique values for filters
      const uniqueAgents = [...new Set(callsResponse.data?.map(call => call.agent?.name).filter(Boolean))];
      const uniqueCampaigns = [...new Set(callsResponse.data?.map(call => call.campaign).filter(Boolean))];
      const uniqueDispositions = [...new Set(callsResponse.data?.map(call => call.disposition).filter(Boolean))];

      setAgents(uniqueAgents);
      setCampaigns(uniqueCampaigns);
      setDispositions(uniqueDispositions);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = async (format = 'excel') => {
    try {
      setExporting(true);
      await exportQAReport(filters, format);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const getScoreBadgeColor = (score, scoreValue = null) => {
    // Handle categorical scores
    if (scoreValue && typeof scoreValue === 'string') {
      switch (scoreValue.toLowerCase()) {
        case 'good':
          return { backgroundColor: '#2196F3', color: 'white' }; // Blue
        case 'average':
          return { backgroundColor: '#FFD700', color: 'black' }; // Yellow
        case 'poor':
          return { backgroundColor: '#9E9E9E', color: 'white' }; // Grey
        case 'rejected':
          return { backgroundColor: '#F44336', color: 'white' }; // Red
        default:
          return { backgroundColor: '#f5f5f5', color: '#666' }; // Default grey
      }
    }
    
    // Handle numerical scores - convert to categorical colors
    if (score >= 80) return { backgroundColor: '#2196F3', color: 'white' }; // Blue (Good)
    if (score >= 60) return { backgroundColor: '#FFD700', color: 'black' }; // Yellow (Average)
    return { backgroundColor: '#F44336', color: 'white' }; // Red (Rejected)
  };

  const getScoreBand = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Average';
    return 'Poor';
  };

  const formatScoreDistributionData = (analytics) => {
    if (!analytics?.scoreDistribution) return [];
    
    const distribution = analytics.scoreDistribution;
    return [
      { name: 'Excellent (90+)', value: distribution.excellent, color: SCORE_COLORS.excellent },
      { name: 'Good (80-89)', value: distribution.good, color: SCORE_COLORS.good },
      { name: 'Average (70-79)', value: distribution.average, color: SCORE_COLORS.average },
      { name: 'Poor (<70)', value: distribution.poor, color: SCORE_COLORS.poor }
    ];
  };

  const formatAgentPerformanceData = (analytics) => {
    if (!analytics?.agentPerformance) return [];
    
    return Object.entries(analytics.agentPerformance).map(([agentId, data]) => ({
      name: data.name,
      averageScore: Math.round(data.averageScore),
      totalCalls: data.totalCalls,
      analyzedCalls: data.analyzedCalls
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
          <Button onClick={fetchDashboardData} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  const analytics = dashboardData?.analytics || {};
  const criteria = dashboardData?.criteria || {};

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box mb={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              QA Dashboard
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Comprehensive Quality Assurance Analytics & Performance Insights
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDashboardData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('excel')}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export Report'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Agent</InputLabel>
              <Select
                value={filters.agent}
                label="Agent"
                onChange={(e) => handleFilterChange('agent', e.target.value)}
              >
                <MenuItem value="">All Agents</MenuItem>
                {agents.map(agent => (
                  <MenuItem key={agent} value={agent}>{agent}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Campaign</InputLabel>
              <Select
                value={filters.campaign}
                label="Campaign"
                onChange={(e) => handleFilterChange('campaign', e.target.value)}
              >
                <MenuItem value="">All Campaigns</MenuItem>
                {campaigns.map(campaign => (
                  <MenuItem key={campaign} value={campaign}>{campaign}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Score Band</InputLabel>
              <Select
                value={filters.scoreBand}
                label="Score Band"
                onChange={(e) => handleFilterChange('scoreBand', e.target.value)}
              >
                <MenuItem value="">All Scores</MenuItem>
                <MenuItem value="excellent">Excellent (90+)</MenuItem>
                <MenuItem value="good">Good (80-89)</MenuItem>
                <MenuItem value="average">Average (70-79)</MenuItem>
                <MenuItem value="poor">Poor (&lt;70)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Calls
                  </Typography>
                  <Typography variant="h4">
                    {analytics.totalCalls || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PhoneIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Analyzed Calls
                  </Typography>
                  <Typography variant="h4">
                    {analytics.analyzedCalls || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.totalCalls > 0 
                      ? `${Math.round((analytics.analyzedCalls / analytics.totalCalls) * 100)}% of total`
                      : '0% of total'
                    }
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <AssessmentIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Average Score
                  </Typography>
                  <Typography variant="h4">
                    {Math.round(analytics.averageScore || 0)}%
                  </Typography>
                  <Chip 
                    label={getScoreBand(analytics.averageScore || 0)}
                    sx={getScoreBadgeColor(analytics.averageScore || 0)}
                    size="small"
                  />
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <StarIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Excellent Calls
                  </Typography>
                  <Typography variant="h4">
                    {analytics.scoreDistribution?.excellent || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.analyzedCalls > 0 
                      ? `${Math.round(((analytics.scoreDistribution?.excellent || 0) / analytics.analyzedCalls) * 100)}% of analyzed`
                      : '0% of analyzed'
                    }
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Score Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Score Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={formatScoreDistributionData(analytics)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ value, percent }) => value > 0 ? `${value} (${(percent * 100).toFixed(1)}%)` : ''}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  minAngle={15}
                >
                  {formatScoreDistributionData(analytics).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value, name) => [`${value} calls`, name]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Custom Legend */}
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={1}>
                {formatScoreDistributionData(analytics).map((entry, index) => (
                  <Grid item xs={6} key={index}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          backgroundColor: entry.color,
                          borderRadius: '2px'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {entry.name}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Agent Performance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Agent Performance
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={formatAgentPerformanceData(analytics)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="averageScore" fill="#8884d8" name="Average Score" />
                <Bar dataKey="analyzedCalls" fill="#82ca9d" name="Analyzed Calls" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Calls Table */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Recent QA Reviews
          </Typography>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => navigate('/qa-review')}
          >
            View All
          </Button>
        </Stack>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Phone Number</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Disposition</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>QA Score</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.slice(0, 10).map((call) => (
                <TableRow key={call.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {call.phoneNumber || call.phone || call.Lead?.phone_number || 
                       (call.unique_id && call.unique_id.match(/\d{10,}/)?.[0]) || 
                       'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2">
                        {call.agent?.name || 'Unknown'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={call.campaign || 'N/A'} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={call.disposition || 'N/A'} 
                      size="small" 
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    {call.duration ? `${Math.round(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {call.qa_score ? (
                      <Chip
                        label={typeof call.qa_score === 'number' ? `${call.qa_score}%` : call.qa_score}
                        sx={getScoreBadgeColor(
                          typeof call.qa_score === 'number' ? call.qa_score : 
                          call.qa_score === 'Good' ? 85 : 
                          call.qa_score === 'Average' ? 70 : 
                          call.qa_score === 'Rejected' ? 40 : 0,
                          call.qa_score
                        )}
                        size="small"
                      />
                    ) : (
                      <Chip 
                        label="Not Evaluated" 
                        size="small" 
                        sx={{ backgroundColor: '#f5f5f5', color: '#666' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(call.created_at), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Review Call">
                        <IconButton 
                          size="small"
                          onClick={() => navigate(`/qa-review/${call.id}`)}
                        >
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default QADashboard; 